import React from "react";
import ReactModal, { Props } from "react-modal";
import styled, { createGlobalStyle } from "styled-components";

import { Fonts } from "assets/fonts";
import { CrossIcon } from "assets/icons";
import { animateColorsOnHover } from "ui/styles/style-snippets";

import { rootSetup } from "./root-setup";

const GlobalModalStyles = createGlobalStyle`
  body.ReactModal__Body--open {
    overflow: hidden;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.2s;
  overflow-y: auto;

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }
  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

const ModalWrapper = styled.div`
  display: flex;
  position: absolute;
  left: 50%;
  top: 0;
  padding: 60px 0;
  transform: translateX(-50%);
  max-width: 80%;
  height: fit-content;
  transition: top 0.2s;
  outline: none;
`;

const ContentWrapper = styled.article`
  position: relative;
  width: 600px;
  padding: 24px 32px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.bgHighestContrast};
  ${Fonts.regular_14};
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 10;
  top: 16px;
  right: -12px;
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: ${(p) => p.theme.textHighestContrastReversed};
  ${animateColorsOnHover}

  &:hover {
    color: ${(p) => p.theme.primaryHighestContrast};
  }

  &:focus {
    outline: 1px dotted ${(p) => p.theme.borderMediumContrast};
    color: ${(p) => p.theme.primaryHighestContrast};
  }
`;

interface ModalProps extends Props {
  className?: string;
  label: string;
  renderFooter?: () => React.ReactNode;
}

export const Modal = (props: ModalProps) => {
  const { className, children, label, ...other } = props;
  const cn = className ? className : "modal";

  // FYI: http://reactcommunity.org/react-modal/accessibility/
  // If setAppElement is missing: https://github.com/reactjs/react-modal/issues/632
  ReactModal.setAppElement(rootSetup());

  return (
    <>
      <GlobalModalStyles />
      <ReactModal
        {...other}
        contentLabel={label}
        className={`${cn}__rm-content`}
        overlayClassName={`${cn}__rm-overlay`}
        closeTimeoutMS={200} // must be the same value as transition in styles: http://reactcommunity.org/react-modal/styles/transitions/
        overlayElement={(overlayProps, contentElement) => (
          <ModalOverlay {...overlayProps}>{contentElement}</ModalOverlay>
        )}
        contentElement={(contentProps, contentChildren) => (
          <ModalWrapper {...contentProps}>{contentChildren}</ModalWrapper>
        )}
      >
        <CloseButton title="Close modal" onClick={other.onRequestClose}>
          <CrossIcon />
        </CloseButton>
        <ContentWrapper className={`${cn}_content-wrapper`}>{children}</ContentWrapper>
      </ReactModal>
    </>
  );
};
